import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const Login = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [activeTab, setActiveTab] = useState('login');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Login form state
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  // Register form state
  const [registerData, setRegisterData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(loginData.email, loginData.password);
      toast.success('Login successful!');
      navigate('/portal');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerData.password !== registerData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (registerData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await register(
        registerData.name,
        registerData.email,
        registerData.phone,
        registerData.password
      );
      toast.success('Registration successful!');
      navigate('/portal');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0d2338] via-[#1E2A4A] to-[#4A6FA5] flex items-center justify-center p-4" data-testid="login-page">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-20 w-72 h-72 rounded-full bg-[#4A6FA5] blur-3xl" />
        <div className="absolute bottom-20 right-20 w-96 h-96 rounded-full bg-[#4A6FA5] blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Back Button */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-6 text-sm transition-colors"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        {/* Card */}
        <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <svg width="48" height="48" viewBox="0 0 100 100" fill="none" className="mx-auto mb-4">
              <path d="M50 8 L78 20 L78 48 Q78 72 50 84 Q22 72 22 48 L22 20 Z" fill="#E8EEF5" opacity=".18"/>
              <path d="M50 8 L78 20 L78 48 Q78 72 50 84 Q22 72 22 48 L22 20 Z" stroke="#1E2A4A" strokeWidth="2.5" fill="none" strokeLinejoin="round"/>
              <line x1="50" y1="28" x2="50" y2="64" stroke="#4A6FA5" strokeWidth="3.5" strokeLinecap="round"/>
              <line x1="33" y1="46" x2="67" y2="46" stroke="#4A6FA5" strokeWidth="3.5" strokeLinecap="round"/>
              <circle cx="50" cy="46" r="6" fill="#4A6FA5"/>
            </svg>
            <h1 className="font-['Cormorant_Garamond'] text-2xl font-semibold text-[#1E2A4A]">
              Patient Portal
            </h1>
            <p className="text-sm text-[#7A8FA0] mt-1">
              Avyan Orthopaedics
            </p>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
            </TabsList>

            {/* Login Form */}
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label className="text-[10px] tracking-wide uppercase text-[#7A8FA0]">
                    Email Address
                  </Label>
                  <Input
                    type="email"
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    placeholder="your@email.com"
                    required
                    className="mt-1"
                    data-testid="login-email"
                  />
                </div>
                <div>
                  <Label className="text-[10px] tracking-wide uppercase text-[#7A8FA0]">
                    Password
                  </Label>
                  <div className="relative mt-1">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      placeholder="••••••••"
                      required
                      data-testid="login-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7A8FA0] hover:text-[#1E2A4A]"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#1E2A4A] hover:bg-[#4A6FA5] text-white py-6"
                  data-testid="login-submit"
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>
            </TabsContent>

            {/* Register Form */}
            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label className="text-[10px] tracking-wide uppercase text-[#7A8FA0]">
                    Full Name
                  </Label>
                  <Input
                    type="text"
                    value={registerData.name}
                    onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                    placeholder="Your full name"
                    required
                    className="mt-1"
                    data-testid="register-name"
                  />
                </div>
                <div>
                  <Label className="text-[10px] tracking-wide uppercase text-[#7A8FA0]">
                    Email Address
                  </Label>
                  <Input
                    type="email"
                    value={registerData.email}
                    onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                    placeholder="your@email.com"
                    required
                    className="mt-1"
                    data-testid="register-email"
                  />
                </div>
                <div>
                  <Label className="text-[10px] tracking-wide uppercase text-[#7A8FA0]">
                    Phone Number
                  </Label>
                  <Input
                    type="tel"
                    value={registerData.phone}
                    onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                    placeholder="+91 XXXXX XXXXX"
                    required
                    className="mt-1"
                    data-testid="register-phone"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[10px] tracking-wide uppercase text-[#7A8FA0]">
                      Password
                    </Label>
                    <Input
                      type="password"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      placeholder="••••••••"
                      required
                      className="mt-1"
                      data-testid="register-password"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-wide uppercase text-[#7A8FA0]">
                      Confirm
                    </Label>
                    <Input
                      type="password"
                      value={registerData.confirmPassword}
                      onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                      placeholder="••••••••"
                      required
                      className="mt-1"
                      data-testid="register-confirm-password"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#1E2A4A] hover:bg-[#4A6FA5] text-white py-6"
                  data-testid="register-submit"
                >
                  {loading ? 'Creating account...' : 'Create Account'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          {/* Footer */}
          <p className="text-center text-xs text-[#7A8FA0] mt-6">
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
